import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LOGIN } from "./RoutesUrl";
import { PostDetailContext, NavigationContext } from "../App";
import PostDetailModal from "../components/common/PostDetailModal";
import { hasUserCompletedSpecialization, isNewUser, setReturnPath } from "../utils/specializationUtils";
import { getUserData } from "./EncryptedLocalStorage";

export const CountContext = createContext();

const ProtectedRoute = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { postDetail, isOpen, postModalClose } = useContext(PostDetailContext);
  const { getReturnUrl, updateCurrentUrl } = useContext(NavigationContext);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [count, setCount] = useState(null);
  const [isCheckingSpecialization, setIsCheckingSpecialization] = useState(true);
  const [hasCheckedInitially, setHasCheckedInitially] = useState(false);

  const updateCount = (val) => {
    setCount(val);
  };



  const checkSpecializationStatus = (userToken) => {
    const currentPath = location.pathname;
    const SPECIALIZATION_ROUTE = "/specialization";
    const DEFAULT_FEED_PATH = "/feed";

    // Use utility function to check completion status
    const hasCompleted = hasUserCompletedSpecialization();
    let userIsNew;

    (async () => {
      userIsNew = await Promise.resolve(isNewUser(userToken));
    })();

    // Case 1: User hasn't completed specialization and is not on specialization page
    if (!hasCompleted && currentPath !== SPECIALIZATION_ROUTE) {

      // Determine return URL - for new users, default to feed
      let returnUrl;
      if (userIsNew || currentPath === LOGIN || currentPath === "/") {
        returnUrl = DEFAULT_FEED_PATH;
      } else {
        returnUrl = getReturnUrl() || currentPath || DEFAULT_FEED_PATH;
      }


      // Store the return path
      setReturnPath(returnUrl);
      localStorage.setItem("returnAfterSpecialization", returnUrl);

      // Navigate to specialization page
      navigate(SPECIALIZATION_ROUTE, {
        state: { from: returnUrl },
        replace: true
      });

      setIsCheckingSpecialization(false);
      return;
    }

    // Case 2: User has completed specialization but is still on specialization page
    if (hasCompleted && currentPath === SPECIALIZATION_ROUTE) {
      // Get the stored return path
      const storedReturnPath = localStorage.getItem("returnAfterSpecialization");
      // Determine where to redirect
      let redirectPath = storedReturnPath || getReturnUrl() || DEFAULT_FEED_PATH;

      // Clean up the stored path
      localStorage.removeItem("returnAfterSpecialization");
      // Navigate to the return path
      navigate(redirectPath, { replace: true });

      setIsCheckingSpecialization(false);
      return;
    }

    // Case 3: User has completed specialization and is not on specialization page
    if (hasCompleted && currentPath !== SPECIALIZATION_ROUTE) {

      // Clean up any leftover return path
      localStorage.removeItem("returnAfterSpecialization");
    }
    setIsCheckingSpecialization(false);
    setHasCheckedInitially(true);
  };

  // Update current URL when location changes
  useEffect(() => {
    updateCurrentUrl(location.pathname);
  }, [location.pathname, updateCurrentUrl]);

  // Set body background color based on login status
  useEffect(() => {
    if (isLoggedIn) {
      document.body.style.backgroundColor = "#f0f7fa";
    } else {
      document.body.style.backgroundColor = "#1dc1dd";
    }

    return () => {
      document.body.style.backgroundColor = "#1dc1dd";
    };
  }, [isLoggedIn]);

  // Initial check when component mounts
  useEffect(() => {
    const checkUser = async () => {
      const userToken = await getUserData()
      if (!userToken?.data?.id) {
        setIsLoggedIn(false);
        setIsCheckingSpecialization(false);
        return navigate(LOGIN);
      }
      setIsLoggedIn(true);

      // Check if user needs to complete specialization
      checkSpecializationStatus(userToken);
    };
    checkUser();
  }, []);

  // Re-check when location changes (but only after initial check)
  useEffect(() => {
    const checkUserStatus = async () => {
      if (hasCheckedInitially && isLoggedIn) {
        const userToken = await getUserData();
        if (userToken?.data?.id) {
          setIsCheckingSpecialization(true);
          checkSpecializationStatus(userToken);
        }
      }
    };

    checkUserStatus(); // call the async function
  }, [location.pathname, hasCheckedInitially, isLoggedIn]);


  // Show loading while checking specialization status
  if (isCheckingSpecialization) {
    return (
      <div className="protectedRoute-centerWrapper">
      </div>
    );
  }

  return (
    <>
      {isLoggedIn ? (
        <CountContext.Provider value={{ count, updateCount }}>
          {isOpen && <PostDetailModal isOpen={isOpen} onClose={postModalClose} post={postDetail} />}
          {props.children}
        </CountContext.Provider>
      ) : null}
    </>
  );
};

export default ProtectedRoute;